import React from 'react'
import { Case, Switch } from 'react-if'
import { LinkButton } from '@/atoms/Button'
import { GuildHighResShieldIcon } from '@/atoms/Icons/GuildHighResShieldIcon'
import { GuildIcon } from '@/atoms/Icons/GuildIcon'
import { LoadingIcon } from '@/atoms/Icons/LoadingIcon/LoadingIcon'
import { VisibilityOffIcon } from '@/atoms/Icons/VibilityOffIcon'
import { Placeholder } from '@/atoms/Placeholder'
import { HeadingXL, HeadingXS, LabelSM, TitleMD } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { LivestreamCountdown } from '@/organisms/Livestream/LivestreamCountdown'
import { LivestreamError } from '@/organisms/Livestream/LivestreamError/LivestreamError'
import { LivestreamOver } from '@/organisms/Livestream/LivestreamOver'
import { LivestreamMode, useLivestream } from '@/organisms/Livestream/LivestreamProvider'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { LivestreamPlayer } from './LivestreamPlayer'

interface LivestreamProps {
  livestreamMeta: {
    guid: string
    bannerCloudinaryPath: string
    startAt: string
    name: string
    url: string
    projectSlug: string
    description: string
  }
  streamId: string
}

export const Livestream: React.FC<LivestreamProps> = ({ livestreamMeta, streamId }) => {
  const { livestream, mode } = useLivestream()
  const { t } = useTranslate('livestream')

  return (
    <Switch>
      <Case condition={mode === LivestreamMode.UNAVAILABLE_GEOGATED}>
        <div
          className="aspect-h-9 aspect-w-16 relative bg-cover"
          style={{
            backgroundImage: livestream?.bannerCloudinaryPath
              ? `url(${getCloudinaryImageUrl({ path: livestream?.bannerCloudinaryPath })})`
              : undefined,
          }}
        >
          <div className="absolute left-1/2 top-1/2 flex h-full w-full -translate-x-1/2 -translate-y-1/2 items-center justify-center backdrop-blur-md">
            <div className="flex flex-col items-center gap-4">
              <VisibilityOffIcon size={64} />
              <HeadingXL weight="bold">Unfortunately,</HeadingXL>
              <TitleMD weight="medium" className="max-w-[406px] text-center">
                geographical restrictions prevent access to this livestream. We hope you find other exciting content to
                enjoy.
              </TitleMD>
            </div>
          </div>
        </div>
      </Case>
      <Case condition={mode === LivestreamMode.GUILD_EXCLUSIVE}>
        <div
          className="aspect-h-9 aspect-w-16 relative bg-cover"
          style={{
            backgroundImage: livestream?.bannerCloudinaryPath
              ? `url(${getCloudinaryImageUrl({ path: livestream?.bannerCloudinaryPath })})`
              : undefined,
          }}
        >
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-xl">
            <div
              className="flex flex-col items-center gap-4"
              style={{
                border:
                  '2px solid background: linear-gradient(172.61deg, #FFE8CC 3.03%, #DDAA85 13.48%, #5F3116 85.07%);\n',
              }}
            >
              <div className="flex items-center gap-2 md:flex-col">
                <GuildHighResShieldIcon size={72} className="h-8 w-8 md:h-auto md:w-auto" />
                <HeadingXS weight="bold" className="md:photon-heading-xl">
                  <Translate t={t} i18nKey="exclusiveLivestream">
                    Exclusive Livestream
                  </Translate>
                </HeadingXS>
              </div>
              <LabelSM weight="medium" className="md:photon-title-xs max-w-[371px] text-center md:mb-8">
                <Translate t={t} i18nKey="guildMembersGetExclusiveAccess">
                  Angel Guild members get exclusive access to livestreams with actors, filmmakers, live recordings of
                  comedy shows and more!
                </Translate>
              </LabelSM>
              <div className="flex gap-4">
                <LinkButton
                  href={`${paths.guild.join}?utm_campaign=guild-memberships&utm_source=exclusive-livestream&utm_content=${livestream?.guid}&utm_medium=web&utm_term=${livestream?.projectSlug}#plan-selection`}
                  variant="oxide-bright"
                  className="flex items-center gap-1"
                >
                  <GuildIcon color1="core-gray-950" />
                  <Translate i18nKey="joinTheGuild" t={t}>
                    Join the Guild
                  </Translate>
                </LinkButton>
                <LinkButton
                  href={`${paths.guild.join}?utm_campaign=guild-memberships&utm_source=exclusive-livestream&utm_content=${livestream?.guid}&utm_medium=web&utm_term=${livestream?.projectSlug}`}
                  variant="core-gray-800"
                >
                  <Translate i18nKey="learnMore" t={t}>
                    Learn More
                  </Translate>
                </LinkButton>
              </div>
            </div>
          </div>
        </div>
      </Case>
      <Case condition={mode === LivestreamMode.PLAY}>
        <LivestreamPlayer
          name={livestreamMeta.name}
          url={livestreamMeta.url}
          description={livestreamMeta.description}
          guid={livestreamMeta.guid}
        />
      </Case>
      <Case condition={mode === LivestreamMode.LOADING}>
        <div className="relative">
          <Placeholder className="aspect-h-9 aspect-w-16 lg:aspect-h-unset lg:aspect-w-unset lg:h-[60vh] lg:w-full" />
          <LoadingIcon className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
        </div>
      </Case>
      <Case condition={mode === LivestreamMode.COUNT_DOWN}>
        <LivestreamCountdown
          bannerCloudinaryPath={livestreamMeta.bannerCloudinaryPath}
          description={livestreamMeta.description}
          name={livestreamMeta.name}
          startAt={livestreamMeta.startAt}
          streamId={streamId}
          projectSlug={livestreamMeta.projectSlug}
        />
      </Case>
      <Case condition={mode === LivestreamMode.ERROR}>
        <LivestreamError />
      </Case>
      <Case condition={mode === LivestreamMode.STREAM_OVER}>
        <LivestreamOver bannerCloudinaryPath={livestreamMeta.bannerCloudinaryPath} />
      </Case>
    </Switch>
  )
}
